import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChartLine,
  faBuilding,
  faUser,
  faKey,
  faCommentDots,
  faGear,
  faClipboardList,
  faBolt,
} from '@fortawesome/free-solid-svg-icons'
import { useAuth } from '../contexts/AuthContext'
import ThemeToggle from '../components/ThemeToggle'
import { useSupabaseQuery, useSupabaseMutation } from '../hooks/useSupabase'
import DashboardTab from '../components/sms/DashboardTab'
import OrganizationsTab from '../components/sms/OrganizationsTab'
import AccountsTab from '../components/sms/AccountsTab'
import ApiCredentialsTab from '../components/sms/ApiCredentialsTab'
import TemplatesTab from '../components/sms/TemplatesTab'
import ProviderConfigsTab from '../components/sms/ProviderConfigsTab'
import EntriesTab from '../components/sms/EntriesTab'
import EdgeFunctionsTab from '../components/sms/EdgeFunctionsTab'

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: faChartLine, path: 'dashboard' },
  { id: 'organizations', label: 'Organizations', icon: faBuilding, path: 'organizations' },
  { id: 'accounts', label: 'Accounts', icon: faUser, path: 'accounts' },
  { id: 'api-credentials', label: 'API Keys', icon: faKey, path: 'api-keys' },
  { id: 'templates', label: 'Templates', icon: faCommentDots, path: 'templates' },
  { id: 'providers', label: 'Providers', icon: faGear, path: 'providers' },
  { id: 'entries', label: 'SMS Log', icon: faClipboardList, path: 'log' },
  { id: 'functions', label: 'Functions', icon: faBolt, path: 'functions' }
]

export default function SmsService() {
  const navigate = useNavigate()
  const location = useLocation()
  const { signOut } = useAuth()
  const [selectedOrganization, setSelectedOrganization] = useState(null)
  const [selectedAccount, setSelectedAccount] = useState(null)

  // Get current tab from URL
  const currentPath = location.pathname.split('/').pop()
  const activeTab = TABS.find(tab => tab.path === currentPath)?.id || 'dashboard'

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">SMS Service</h1>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 hidden sm:block">Infobip SMS Integration</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3 self-end sm:self-auto">
              <ThemeToggle />
              <button
                onClick={handleSignOut}
                className="px-3 py-2 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg hover:shadow-lg transition"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-4 sm:space-x-8" aria-label="Tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => navigate(`/service-sms/${tab.path}`)}
                className={`
                  py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap
                  ${activeTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }
                `}
              >
                <FontAwesomeIcon icon={tab.icon} className="mr-1 sm:mr-2 w-4 h-4" fixedWidth />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Breadcrumb / Context */}
      {(selectedOrganization || selectedAccount) && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center flex-wrap gap-2 text-xs sm:text-sm">
              <span className="text-gray-600 dark:text-gray-400">Context:</span>
              {selectedOrganization && (
                <>
                  <span className="px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded">
                    Org: {selectedOrganization.Name}
                  </span>
                  {selectedOrganization.Name !== selectedOrganization.Name && (
                    <button
                      onClick={() => {
                        setSelectedOrganization(null)
                        setSelectedAccount(null)
                      }}
                      className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                    >
                      ×
                    </button>
                  )}
                </>
              )}
              {selectedAccount && (
                <>
                  <span className="text-gray-400">→</span>
                  <span className="px-2 py-1 bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 rounded">
                    Account: {selectedAccount.Name}
                  </span>
                  <button
                    onClick={() => setSelectedAccount(null)}
                    className="text-green-600 hover:text-green-800 dark:text-green-400"
                  >
                    ×
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Routes>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<DashboardTab />} />
          <Route
            path="organizations"
            element={
              <OrganizationsTab
                selectedOrganization={selectedOrganization}
                onSelectOrganization={setSelectedOrganization}
              />
            }
          />
          <Route
            path="accounts"
            element={
              <AccountsTab
                selectedOrganization={selectedOrganization}
                selectedAccount={selectedAccount}
                onSelectAccount={setSelectedAccount}
              />
            }
          />
          <Route
            path="api-keys"
            element={
              <ApiCredentialsTab selectedAccount={selectedAccount} />
            }
          />
          <Route
            path="templates/*"
            element={
              <TemplatesTab selectedAccount={selectedAccount} />
            }
          />
          <Route
            path="providers"
            element={<ProviderConfigsTab />}
          />
          <Route
            path="log"
            element={
              <EntriesTab selectedAccount={selectedAccount} />
            }
          />
          <Route
            path="functions"
            element={<EdgeFunctionsTab />}
          />
        </Routes>
      </main>
    </div>
  )
}
